import { observer } from 'mobx-react-lite';
import { createContext, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import 'reflect-metadata';

import { MFErrorText } from '~/components/reuseable/MFErrorText';
import { FileModeEnum } from '~modules/fileTypes/types/FileModeEnum';

import { useMFBaseContext } from '../hooks/useMFBaseContext';
import { MFBaseContextPropsType } from './MFBaseProvider';

export type MFAttachedDocumentsContextPropsType = MFBaseContextPropsType & {
  documentTypeId: number;
  /** ID типа документа - используется для определения, какие типы файлов могут быть прикреплены к данному документу */
  documentId?: number;
  /** ID документа - уникальный идентификатор конкретного документа, к которому прикрепляются файлы */
  documentTitle?: string;
  /** Заголовок документа - отображаемое имя документа, используется если documentId не передан */
  mode: FileModeEnum;
  /** Режим отображения файлов - определяет, какие типы файлов показывать (все, только изображения или документы) */
  readonly?: boolean;
  /** Флаг только для чтения - если true, то пользователь не может загружать, удалять или добавлять файлы */
  includeNonGroupType?: boolean;
  /** Флаг для получения типов прикрепленных файлов 'Без группы' */
  withoutFixedTypes?: boolean;
  /** Флаг исключения фиксированных типов */
  notShowFixTypeLabels?: boolean;
  /** Флаг скрытия наименования текущей группы - если true, то не будут отображаться лейбл с наименованием */
  title?: ReactNode;
  /** Заголовок блока */
};

export type MFAttachedDocumentsProviderPropsType = MFAttachedDocumentsContextPropsType & {
  children: ReactNode;
};

export const MFAttachedDocumentsContext = createContext<MFAttachedDocumentsContextPropsType | undefined>(undefined);

// TS не передается в родительские проекты,
// поэтому определяем проверяем обязательные пропы на undefined
const requiredProps: Partial<keyof MFAttachedDocumentsContextPropsType>[] = ['documentTypeId', 'mode'];

export const MFAttachedDocumentsProvider: FC<MFAttachedDocumentsProviderPropsType> = observer((props) => {
  const mfBaseContext = useMFBaseContext();
  const { t } = useTranslation('common');

  // Проверяем обязательные пропы
  if (requiredProps.some((prop) => props[prop as keyof MFAttachedDocumentsContextPropsType] === undefined)) {
    console.log(t('error.requiredPropsNotFound'));

    return <MFErrorText />;
  }

  // Проверяем, что передан либо documentId, либо documentTitle
  if (!props.documentId && !props.documentTitle) {
    console.log(t('error.requiredPropsNotFound'));

    return <MFErrorText />;
  }

  const contextValue = {
    ...mfBaseContext, // наследуем базовый контекст
    ...props,
  };

  return (
    <MFAttachedDocumentsContext.Provider value={contextValue}>{props.children}</MFAttachedDocumentsContext.Provider>
  );
});
